import React, { useCallback, useState } from 'react'
import useInterval from '@/util/hook/useInterval.tsx'
import { IAlertBio } from '@/api/notification/getNotificationBio.ts'
import { equalString } from '@/util/strings.ts'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import AltTooltip from '@/component/tooltip/AltTooltip.tsx'
import TruncatedText from '@/component/tooltip/TruncatedText.tsx'
import { calculateAge } from '@/util/person.ts'
import LocationIcon from '@/assets/ic_alert_location.svg?react'
import { useTranslation } from 'react-i18next'
import useQueryGetMe from '@/api/auth/getMe.ts'
import { getLocationUnavailableText, hasAlertCoordinates } from '@/util/alertLocation.ts'
import { AlertProtecteeGender, AlertVitalStatus } from '@/api/notification/alertServerValues.ts'

interface IProps extends IAlertBio {
    className?: string;
    onRefreshList: () => void
    onClickMarker: (latitude?: number, longitude?: number) => void
    onClickItem: (id: number) => void
}

const UrgentNotification: React.FunctionComponent<IProps> = ({
    id,
    onClickItem,
    className,
    alert,
    protectee,
    onClickMarker
}) => {
    const { t } = useTranslation()
    const { data: me } = useQueryGetMe()
    const isMaskedUser = me?.email === 'dbd_cm@clomental.com'

    const getStableName = useCallback((value: string) => {
        if (equalString(value, AlertVitalStatus.NORMAL)) return t('dashboard.notification.stable')
        return t('dashboard.notification.unstable')
    }, [t])

    const getGenderName = useCallback((value: string) => {
        if (equalString(value, AlertProtecteeGender.MALE)) return t('dashboard.notification.gender.male')
        return t('dashboard.notification.gender.female')
    }, [t])

    const hasLocation = hasAlertCoordinates(alert?.location)
    const [pastTime, setPastTime] = useState('00:00:00')
    const { hasPermission } = useQueryGetMyPermission()

    useInterval(() => {
        const pastSeconds = Math.floor((Date.now() - new Date(alert?.createdAt).getTime()) / 1000)
        const hour = Math.floor(pastSeconds / 3600)
        const minute = Math.floor(pastSeconds % 3600 / 60)
        const second = Math.floor(pastSeconds % 60)
        setPastTime(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`)
    }, 1000)

    const getMaskedName = (name: string) => {
        if (!isMaskedUser || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }

    const getMaskedContact = (contact: string) => {
        if (!isMaskedUser || !contact) return contact

        const contactParts = contact.split('-')
        if (contactParts.length === 3) return `${contactParts[0]}-****-****`
        if (contact.length > 3) return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        return contact
    }

    return (
        <article className={`${className || ''} care-dashboard-alert-card`}>
            <div className="flex items-start justify-between gap-[10px]">
                <div className="min-w-0">
                    <AltTooltip
                        render={<TruncatedText
                            textClassName="text-[14px] font-extrabold text-[#27364B]"
                            text={getMaskedName(protectee?.name)}
                        />}
                    />
                    <p className="mt-[2px] text-[11px] font-medium text-[#738095]">
                        {t('dashboard.notification.age', { count: calculateAge(protectee?.birthDate) || 0 })} · {getGenderName(protectee?.gender)} · {getMaskedContact(protectee?.contact)}
                    </p>
                </div>
                <span className="shrink-0 rounded-full bg-[#C92C35] px-[8px] py-[4px] text-[10px] font-bold text-white">
                    {t('dashboard.notification.pastTime', { time: pastTime })}
                </span>
            </div>

            <div className="mt-[10px] grid grid-cols-2 gap-[7px]">
                {alert?.nameDetail?.hrate && (
                    <div className={`care-dashboard-alert-card__reading ${!equalString(alert?.nameDetail?.hrateStatus, AlertVitalStatus.NORMAL) ? 'text-[#C92C35]' : 'text-[#40516A]'}`}>
                        <span className="block text-[10px] font-semibold text-[#7A8798]">{t('dashboard.notification.heartRate')}</span>
                        <strong className="mt-[2px] block">{alert.nameDetail.hrate} · {getStableName(alert.nameDetail.hrateStatus)}</strong>
                    </div>
                )}
                {alert?.nameDetail?.resp && (
                    <div className={`care-dashboard-alert-card__reading ${!equalString(alert?.nameDetail?.respStatus, AlertVitalStatus.NORMAL) ? 'text-[#C92C35]' : 'text-[#40516A]'}`}>
                        <span className="block text-[10px] font-semibold text-[#7A8798]">{t('dashboard.notification.respiration')}</span>
                        <strong className="mt-[2px] block">{alert.nameDetail.resp} · {getStableName(alert.nameDetail.respStatus)}</strong>
                    </div>
                )}
            </div>

            <div className="mt-[10px] grid grid-cols-2 gap-[7px]">
                <button
                    type="button"
                    className="care-dashboard-alert-card__location disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!hasLocation}
                    title={hasLocation ? undefined : getLocationUnavailableText(t)}
                    onClick={() => onClickMarker(alert?.location?.latitude, alert?.location?.longitude)}
                >
                    {t('dashboard.notification.location')}
                    <LocationIcon className="h-[14px] w-[14px]" />
                </button>
                {hasPermission(PermissionSubject.ALERT_BIO, PermissionAction.UPDATE) && (
                    <button
                        type="button"
                        className="care-dashboard-alert-card__confirm"
                        onClick={() => onClickItem(id)}
                    >
                        {t('dashboard.notification.confirm')}
                    </button>
                )}
            </div>
        </article>
    )
}

export default UrgentNotification
